import type {
  Absence,
  Break,
  BreakRuleTier,
  Meeting,
  Nonbusinessday,
  TimeEntry,
  WorkSchedule,
} from '../../lib/api'
import { metricsForDay, sumMetricsUntilToday } from '../../lib/calendar-overlays'
import { cn, formatHM } from '../../lib/utils'

interface SollIstStripProps {
  days: Date[]
  entries: TimeEntry[]
  breaks: Break[]
  absences: Absence[]
  holidays: Map<string, Nonbusinessday>
  breakTiers: BreakRuleTier[]
  targetHours: readonly WorkSchedule[]
  meetings: Meeting[]
  /** Compact = ein-zeilig für die Header-Position; non-compact = Footer mit
   *  Breakdown (Einträge / Meetings / Abwesenheit). */
  compact?: boolean
  className?: string
}

/** Zusammenfassung Soll vs. Ist über `days`. Rechnet rein client-seitig aus
 *  den schon vom Kalender geladenen Daten — keine extra API-Calls. */
export function SollIstStrip({
  days,
  entries,
  breaks,
  absences,
  holidays,
  breakTiers,
  targetHours,
  meetings,
  compact = false,
  className,
}: SollIstStripProps) {
  // Zukunfts-Cap (Ticket „Sollstunden tagesaktuell“): Tage nach heute tragen
  // weder Soll noch Ist bei — vorher zeigte die laufende Woche/der laufende
  // Monat sofort das volle Zeitraum-Soll und stand dauerhaft im Minus.
  const metricsByDay = days.map((d) =>
    metricsForDay(d, entries, breaks, breakTiers, holidays, absences, targetHours, meetings),
  )
  const sums = sumMetricsUntilToday(days, metricsByDay)
  const totalTarget = sums.targetMinutes
  const totalEntries = sums.entriesMinutes
  const totalMeetings = sums.meetingMinutes
  const totalAbsenceCredit = sums.absenceCreditMinutes
  const totalActual = totalEntries + totalMeetings + totalAbsenceCredit
  const diff = totalActual - totalTarget
  const hasTarget = totalTarget > 0

  if (compact) {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-2 rounded-md border border-border-subtle bg-bg-elevated/60 px-2.5 py-1 text-xs',
          className,
        )}
        title={tooltipText(
          totalTarget,
          totalEntries,
          totalMeetings,
          totalAbsenceCredit,
          diff,
          hasTarget,
        )}
      >
        {/* nowrap: bei knapper Kopfzeile (mobil Monat/Jahr) darf die Zahl
            nicht zweizeilig umbrechen — lieber rechts sauber abschneiden. */}
        <span className="whitespace-nowrap font-mono tabular-nums text-text-secondary">
          {formatHM(totalActual)}
          {hasTarget && (
            <>
              <span className="text-text-tertiary"> / </span>
              {formatHM(totalTarget)}
            </>
          )}
        </span>
        {/* Diff-Badge mobil ausblenden: neben dem Nav-Cluster in Zeile 1
            des Kalender-Kopfs ist nur Platz für Ist/Soll selbst. */}
        {hasTarget && <DiffBadge diff={diff} className="hidden sm:inline-flex" />}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-2xl border border-border-subtle bg-bg-elevated/40 px-4 py-3',
        className,
      )}
    >
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-text-tertiary">Soll/Ist</span>
        <span className="font-mono tabular-nums text-base text-text-primary">
          {formatHM(totalActual)}
          {hasTarget && (
            <>
              <span className="text-text-tertiary"> / </span>
              {formatHM(totalTarget)}
            </>
          )}
        </span>
        {hasTarget && <DiffBadge diff={diff} />}
        <div className="flex-1" />
        <Breakdown label="Einträge" minutes={totalEntries} />
        {totalMeetings > 0 && <Breakdown label="Meetings" minutes={totalMeetings} />}
        {totalAbsenceCredit > 0 && <Breakdown label="Abwesenheit" minutes={totalAbsenceCredit} />}
      </div>
    </div>
  )
}

function Breakdown({ label, minutes }: { label: string; minutes: number }) {
  return (
    <span className="text-[11px] text-text-tertiary">
      <span className="opacity-60">{label}:</span>{' '}
      <span className="font-mono tabular-nums text-text-secondary">{formatHM(minutes)}</span>
    </span>
  )
}

function DiffBadge({ diff, className }: { diff: number; className?: string }) {
  const positive = diff >= 0
  const sign = positive ? '+' : '−'
  const value = formatHM(Math.abs(diff))
  // Tolerance: ±15min → neutral chip. >|15min| → green/red.
  const neutral = Math.abs(diff) < 15
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 font-mono tabular-nums text-[11px]',
        neutral && 'bg-bg-overlay/60 text-text-tertiary',
        !neutral && positive && 'bg-success/15 text-success',
        !neutral && !positive && 'bg-danger/15 text-danger',
        className,
      )}
    >
      {sign}
      {value}
    </span>
  )
}

function tooltipText(
  target: number,
  entries: number,
  meetings: number,
  absenceCredit: number,
  diff: number,
  hasTarget: boolean,
): string {
  const parts = [
    `Soll: ${formatHM(target)}`,
    `Ist: ${formatHM(entries + meetings + absenceCredit)}`,
  ]
  if (entries > 0) parts.push(`  · Einträge: ${formatHM(entries)}`)
  if (meetings > 0) parts.push(`  · Meetings: ${formatHM(meetings)}`)
  if (absenceCredit > 0) parts.push(`  · Abwesenheit: ${formatHM(absenceCredit)}`)
  if (hasTarget) {
    parts.push(`Δ: ${diff >= 0 ? '+' : '−'}${formatHM(Math.abs(diff))}`)
  }
  return parts.join('\n')
}
